import clsx from 'clsx';
import React, { useContext, useEffect, useReducer } from 'react';
import { withRouter } from 'react-router';
import { useHistory } from 'react-router';

import { Card, CardActions, CardContent, Divider, Grid, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

import { ReloadContext } from 'src/contexts/ReloadContext';
import useCatchAPIError from 'src/hooks/useCatchAPIError';
import useGlobalLoadingBar from 'src/hooks/useGlobalLoadingBar';
import useGlobalSnackBar from 'src/hooks/useGlobalSnackBar';
import formErrorFactory from 'src/services/formError.factory';
import vendorAccountApiService from 'src/services/vendorAccount.api.service';
import Button from 'src/wrappers/Button';

const useStyles = makeStyles((theme) => ({
  buttonRight: {
    marginLeft: theme.spacing(1),
  },
  buttons: {
    marginTop: theme.spacing(2),
  },
  card: {
    boxShadow: '0px 7px 8px -4px rgb(0 0 0 / 20%), 0px 12px 17px 2px rgb(0 0 0 / 14%), 0px 5px 22px 4px rgb(0 0 0 / 12%)',
    padding: theme.spacing(3),
  },
  cardActions: {
    justifyContent: 'flex-end',
  },
  newSection: {
    paddingTop: '40px !important',
    paddingBottom: '0px !important',
  },
}));

const vendorAccountEntryReducer = (state, action) => {
  switch (action.type) {
    case 'field': {
      return {
        ...state,
        [action.fieldName]: action.payload,
      };
    }
    case 'loadExistingData': {
      return {
        ...state,
        ...action.payload,
        errors: [],
        isEdit: true,
        isSubmitting: false,
        isSubmitted: false,
      };
    }
    case 'submit': {
      return {
        ...state,
        errors: [],
        isSubmitting: true,
      };
    }
    case 'success': {
      return {
        ...state,
        isSubmitting: false,
        isSubmitted: true,
      };
    }
    case 'error': {
      return {
        ...state,
        errors: action.payload,
        isSubmitting: false,
        isSubmitted: false,
      };
    }
    default:
      return state;
  }
};

const vendorEntryInitialState = {
  accountId: '',
  company: '',
  vendorNumbers: '',
  contactEmail: '',
  username: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  errors: [],
  isEdit: false,
  isSubmitting: false,
  isSubmitted: false,
};

const VendorAccountEntry = ({ vendorAccountId, ...props }) => {
  const classes = useStyles();
  const history = useHistory();
  const [, setReloadContext] = useContext(ReloadContext);
  const { startProgress, stopProgress } = useGlobalLoadingBar();
  const { showSuccessSnackBar } = useGlobalSnackBar();
  const { catchApiError } = useCatchAPIError();

  const [state, dispatch] = useReducer(vendorAccountEntryReducer, vendorEntryInitialState);
  const { accountId, company, vendorNumbers, username, firstName, lastName, phoneNumber, contactEmail, errors, isEdit, isSubmitting } = state;

  useEffect(() => {
    const fetchData = async (accountId) => {
      try {
        startProgress();
        let accountResult = await vendorAccountApiService.getVendorAccount(accountId);
        dispatch({
          type: 'loadExistingData',
          payload: {
            accountId: accountId,
            company: accountResult.data.name,
            vendorNumbers: accountResult.data.vendorNumbers,
            contactEmail: accountResult.data.contactEmail,
          },
        });
        stopProgress();
      } catch (err) {
        catchApiError(err);
      }
    };

    if (vendorAccountId) {
      fetchData(vendorAccountId);
    }
  }, [vendorAccountId]);

  const handleSubmit = (event) => {
    //internal functions
    const handleSubmitResult = (apiResult, successMessage, onSuccess) => {
      stopProgress();
      if (apiResult.errors && apiResult.errors.length > 0) {
        let  { errors } = formErrorFactory.createErrors(apiResult.errors);
        dispatch({
          type: 'error',
          payload: errors,
        });
      } else {
        dispatch({ type: 'success' });
        setReloadContext(new Date());
        showSuccessSnackBar(successMessage);
        if (onSuccess) {
          onSuccess();
        }
      }
    };

    //Submitting
    startProgress();
    event.preventDefault();
    dispatch({ type: 'submit' });
    const formData = new FormData();

    if (!isEdit) {
      vendorAccountApiService
        .createVendorAccount(formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: {
            name: company,
            contactEmail: contactEmail,
            firstName: firstName,
            lastName: lastName,
            phoneNumber: phoneNumber,
            vendorNumbers: vendorNumbers,
            username: username,
          },
        })
        .then((result) => {
          handleSubmitResult(result, `The Vendor was created successfully`, () => {
            history.replace(`/accounts/vendor/${result.data}/edit`);
          });
        })
        .catch((err) => {
          catchApiError(err, () => {
            dispatch({
              type: 'error',
              payload: [],
            });
          });
        });
    } else {
      vendorAccountApiService
        .updateVendorAccount(formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: {
            id: accountId,
            name: company,
            contactEmail: contactEmail,
            vendorNumbers: vendorNumbers,
          },
        })
        .then((result) => {
          handleSubmitResult(result, `The Vendor was updated successfully`);
        })
        .catch((err) => {
          catchApiError(err, () => {
            dispatch({
              type: 'error',
              payload: [],
            });
          });
        });
    }
  };

  return (
    <>
      <form autoComplete="off" onSubmit={handleSubmit} noValidate>
        <Card className={classes.card}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {vendorAccountId ? <h4>Edit Vendor Account</h4> : <h4>Create New Vendor Account</h4>}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Vendor's Name"
                  margin="dense"
                  name="company"
                  onChange={(e) =>
                    dispatch({
                      type: 'field',
                      fieldName: 'company',
                      payload: e.target.value,
                    })
                  }
                  required
                  error={errors['Name'] != null}
                  helperText={errors['Name']}
                  value={company || ''}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Vendor Numbers"
                  margin="dense"
                  name="vendorNumbers"
                  onChange={(e) =>
                    dispatch({
                      type: 'field',
                      fieldName: 'vendorNumbers',
                      payload: e.target.value,
                    })
                  }
                  required
                  error={errors['VendorNumbers'] != null}
                  helperText={errors['VendorNumbers']}
                  value={vendorNumbers || ''}
                  disabled={isSubmitting}
                />
                <Typography variant="caption" display="block" gutterBottom>
                  This numbers will use in searching the Purchase Orders on Shipment Booking
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contact Email"
                  margin="dense"
                  name="contactEmail"
                  type="email"
                  error={errors['ContactEmail'] != null}
                  helperText={errors['ContactEmail']}
                  onChange={(e) =>
                    dispatch({
                      type: 'field',
                      fieldName: 'contactEmail',
                      payload: e.target.value,
                    })
                  }
                  required
                  value={contactEmail || ''}
                  disabled={isSubmitting}
                />
              </Grid>

              {!isEdit && (
                <>
                  <Grid item xs={12} className={classes.newSection}>
                    <strong>User:</strong>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Username (Email)"
                      margin="dense"
                      name="username"
                      type="email"
                      error={errors['Username'] != null}
                      helperText={errors['Username']}
                      onChange={(e) =>
                        dispatch({
                          type: 'field',
                          fieldName: 'username',
                          payload: e.target.value,
                        })
                      }
                      required
                      value={username || ''}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="First Name"
                      margin="dense"
                      name="firstName"
                      error={errors['FirstName'] != null}
                      helperText={errors['FirstName']}
                      onChange={(e) =>
                        dispatch({
                          type: 'field',
                          fieldName: 'firstName',
                          payload: e.target.value,
                        })
                      }
                      value={firstName || ''}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      margin="dense"
                      name="lastName"
                      error={errors['LastName'] != null}
                      helperText={errors['LastName']}
                      onChange={(e) =>
                        dispatch({
                          type: 'field',
                          fieldName: 'lastName',
                          payload: e.target.value,
                        })
                      }
                      value={lastName || ''}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      margin="dense"
                      type="tel"
                      name="phoneNumber"
                      error={errors['PhoneNumber'] != null}
                      helperText={errors['PhoneNumber']}
                      onChange={(e) =>
                        dispatch({
                          type: 'field',
                          fieldName: 'phoneNumber',
                          payload: e.target.value,
                        })
                      }
                      value={phoneNumber || ''}
                      required
                      disabled={isSubmitting}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
          <Divider />
          <CardActions className={classes.cardActions}>
            <Button buttonType="primary" type="submit" disabled={isSubmitting} className={classes.buttons}>
              Save
            </Button>
            <Button buttonType="cancel" className={clsx(classes.buttons, classes.buttonRight)} disabled={isSubmitting} onClick={() => props.history.goBack()}>
              Cancel
            </Button>
          </CardActions>
        </Card>
      </form>
    </>
  );
};

export default withRouter(VendorAccountEntry);
